import React, { useEffect, useState, useRef, useCallback } from "react";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  FolderPlus, FolderOpen, Upload, Search, Loader2, Sparkles,
  LayoutGrid, List as ListIcon, HardDrive, ExternalLink, RefreshCw, X, Cloud, Copy, Check,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ReceivedUnderNdaDialog, ndaBlocks } from "@/components/nda/NdaBits";
import { useOpenGesture, AddressBar, FolderPane, FolderItem, DocItem, SheetItem, FolderTile, SheetTile, DocTile } from "@/components/docs/ExplorerBits";
import { DocHistoryDialog } from "@/components/docs/DocHistoryDialog";

const CHUNK = 4 * 1024 * 1024; // 4MB — stays under ingress request limits
const GRID = "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3";
const locKey = (bidId) => `gf-docs-loc-${bidId}`;
const readLoc = (bidId) => { try { return JSON.parse(sessionStorage.getItem(locKey(bidId))) || null; } catch { return null; } };
const slim = (d) => d && { document_id: d.document_id, name: d.name, folder_id: d.folder_id, kind: d.kind, size: d.size, sheet_count: d.sheet_count, cover_sheet_id: d.cover_sheet_id, storage_backend: d.storage_backend };

export default function DocumentsTab({ bid, readOnly, canDelete, onOpenSheet }) {
  const bidId = bid.bid_id;
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(() => readLoc(bidId)?.f || "");           // "" = root
  const [openDoc, setOpenDoc] = useState(() => readLoc(bidId)?.d || null);       // a plan set opened like a folder
  const [hist, setHist] = useState(() => readLoc(bidId)?.hist || { stack: [{ f: "", d: null }], i: 0 });
  const [foldersReady, setFoldersReady] = useState(false);
  const [docsAt, setDocsAt] = useState(null);
  const docsReq = useRef(0);
  const explorerRef = useRef(null);
  const [selected, setSelected] = useState(null);     // "f:<id>" | "d:<id>" | "s:<id>"
  const [renaming, setRenaming] = useState(null);
  const gesture = useOpenGesture();
  const [uploading, setUploading] = useState(null);   // {name, pct}
  const [ndaState, setNdaState] = useState(null);
  const [ndaAsk, setNdaAsk] = useState(null);         // { filename, resolve }
  useEffect(() => { api.get(`/bids/${bidId}/nda`).then((r) => setNdaState(r.data)).catch(() => setNdaState(null)); }, [bidId]);
  // When the client has no active NDA, ask whether this file came in under one; the answer is kept on the document.
  const askUnderNda = (filename) => (ndaState && ndaBlocks(ndaState.state) && !ndaState.override
    ? new Promise((resolve) => setNdaAsk({ filename, resolve })) : Promise.resolve(null));
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [sheetsMap, setSheetsMap] = useState({});
  const [view, setView] = useState(() => readLoc(bidId)?.view || "list");        // "list" | "grid"
  const [progress, setProgress] = useState(null);
  const [jobActive, setJobActive] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [driveEnabled, setDriveEnabled] = useState(false);
  const syncedOnce = useRef(false);
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [rawUpload, setRawUpload] = useState(false);
  const [driveDialog, setDriveDialog] = useState(false);
  const [driveFolder, setDriveFolder] = useState(null);
  const [ingestingDrive, setIngestingDrive] = useState(false);
  const [driveProgress, setDriveProgress] = useState(null);
  const [copied, setCopied] = useState(false);
  const [historyOf, setHistoryOf] = useState(null);

  const loadFolders = useCallback(() => api.get(`/bids/${bidId}/folders`).then((r) => { setFolders(r.data); setFoldersReady(true); }).catch(() => {}), [bidId]);
  const loadDocs = useCallback(() => {
    const id = ++docsReq.current;
    return api.get(`/bids/${bidId}/documents`, { params: { folder_id: active } })
      .then((r) => { if (id === docsReq.current) { setDocs(r.data); setDocsAt(active); } }).catch(() => {});
  }, [bidId, active]);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => {
    try { sessionStorage.setItem(locKey(bidId), JSON.stringify({ f: active, d: slim(openDoc), hist, view })); }
    catch { sessionStorage.removeItem(locKey(bidId)); }
  }, [bidId, active, openDoc, hist, view]);

  const loadSheets = useCallback((docId) => api.get(`/bids/${bidId}/sheets`, { params: { document_id: docId } }).then((r) => setSheetsMap((m) => ({ ...m, [docId]: r.data }))).catch(() => {}), [bidId]);
  const openDocId = openDoc?.document_id;
  useEffect(() => { if (openDocId) loadSheets(openDocId); }, [openDocId, loadSheets]);

  // detect an already-running naming/OCR job when the tab opens (it may span other folders)
  useEffect(() => {
    api.get(`/bids/${bidId}/doc-progress`).then((r) => {
      if ((r.data.busy || 0) > 0 || r.data.job?.status === "running") { setProgress(r.data); setJobActive(true); }
    }).catch(() => {});
  }, [bidId]);

  // poll doc-progress while a naming/OCR run is active (across all folders) and refresh views
  useEffect(() => {
    const localBusy = docs.some((d) => d.ai_status === "processing" || d.ocr_status === "processing");
    if (!jobActive && !localBusy) { setProgress(null); return; }
    let stop = false;
    api.get(`/bids/${bidId}/doc-progress`).then((r) => {
      if (stop) return;
      setProgress(r.data);
      setJobActive((r.data.busy || 0) > 0 || r.data.job?.status === "running");
    }).catch(() => {});
    const t = setTimeout(() => {
      loadDocs();
      if (openDoc) loadSheets(openDoc.document_id);
    }, 2500);
    return () => { stop = true; clearTimeout(t); };
  }, [docs, jobActive, loadDocs, openDoc, loadSheets, bidId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (docs.some((d) => d.storage_backend === "drive")) setDriveEnabled(true); }, [docs]);

  const aiName = async (d) => {
    try {
      await api.post(`/bids/${bidId}/documents/${d.document_id}/ai-name`);
      toast.success(`Reading title blocks on ${d.name}…`);
      setDocs((ds) => ds.map((x) => x.document_id === d.document_id ? { ...x, ai_status: "processing", ai_progress: 0 } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start AI naming");
    }
  };

  const runOcr = async (d) => {
    try {
      await api.post(`/bids/${bidId}/documents/${d.document_id}/ocr`);
      toast.success(`Extracting text from ${d.name}…`);
      setDocs((ds) => ds.map((x) => x.document_id === d.document_id ? { ...x, ocr_status: "processing", ocr_progress: 0 } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start OCR");
    }
  };

  const processAll = async () => {
    if (!window.confirm("Auto-name every sheet from its title block and make the whole packet searchable (OCR scanned pages)? Names fill in first, then search text. You can cancel anytime.")) return;
    try {
      const { data } = await api.post(`/bids/${bidId}/documents/process-all`);
      toast.success(`Processing ${data.count} document${data.count === 1 ? "" : "s"} — names fill in first, then search text`);
      setJobActive(true);
      setDocs((ds) => ds.map((x) => (x.kind === "pdf" || x.kind === "image") ? { ...x, ai_status: "processing", ai_progress: 0 } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to process");
    }
  };

  const cancelProcessing = async () => {
    try {
      await api.post(`/bids/${bidId}/documents/cancel-processing`);
      toast.success("Stopping — sheets already done are kept; no new ones will start");
      setJobActive(false);
      setProgress(null);
      loadDocs();
    } catch { toast.error("Could not cancel processing"); }
  };

  const renameSheet = async (docId, sheetId, name) => {
    try {
      await api.put(`/bids/${bidId}/sheets/${sheetId}`, { name });
      setSheetsMap((m) => ({ ...m, [docId]: (m[docId] || []).map((s) => s.sheet_id === sheetId ? { ...s, name } : s) }));
    } catch (e) {
      toast.error("Could not rename sheet");
      loadSheets(docId);
    }
  };

  const uploadOne = async (file) => {
    const underNda = await askUnderNda(file.name);
    setUploading({ name: file.name, pct: 0 });
    try {
      const { data: { upload_id } } = await api.post(`/bids/${bidId}/uploads/init`, { filename: file.name, size: file.size, folder_id: active || null });
      const chunks = Math.max(1, Math.ceil(file.size / CHUNK));
      for (let i = 0; i < chunks; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        let attempt = 0;
        for (;;) {
          try {
            await api.put(`/uploads/${upload_id}/chunk?index=${i}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
            break;
          } catch (err) {
            if (++attempt >= 3) throw err;
            await new Promise((r) => setTimeout(r, 800 * attempt));
          }
        }
        setUploading({ name: file.name, pct: Math.round(((i + 1) / chunks) * 100) });
      }
      const { data } = await api.post(`/bids/${bidId}/uploads/${upload_id}/complete`, { filename: file.name, content_type: file.type || "application/octet-stream", folder_id: active || null, raw: rawUpload, received_under_nda: underNda });
      // ingest runs in the background — poll until it finishes so big zips don't time out
      const jobId = data.job_id;
      if (jobId) {
        setUploading({ name: file.name, pct: 100, phase: "Processing files…" });
        let misses = 0;
        for (;;) {
          await new Promise((r) => setTimeout(r, 2000));
          let st;
          try { st = (await api.get(`/bids/${bidId}/uploads/${jobId}/status`)).data; misses = 0; }
          catch { if (++misses >= 20) { toast.info(`${file.name} is still processing in the background — it’ll appear shortly.`); break; } continue; }
          if (st.total) setUploading({ name: file.name, pct: 100, phase: `Processing ${st.done}/${st.total}…` });
          if (st.status === "done") {
            toast.success(`${file.name} — added ${st.count} item${st.count === 1 ? "" : "s"}`);
            if (st.skipped?.length) toast.warning(`${st.skipped.length} file${st.skipped.length === 1 ? "" : "s"} in ${file.name} couldn’t be added: ${st.skipped.slice(0, 3).join(", ")}${st.skipped.length > 3 ? "…" : ""}`);
            break;
          }
          if (st.status === "failed") { toast.error(st.error || `Could not process ${file.name}`); break; }
        }
      } else {
        toast.success(`${file.name} — added ${data.count} item${data.count === 1 ? "" : "s"}`);
      }
      await loadFolders();
      await loadDocs();
    } catch (e) {
      toast.error(`Upload failed: ${file.name}`);
    } finally {
      setUploading(null);
    }
  };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) await uploadOne(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  const newFolder = async () => {
    const name = window.prompt("New folder name:", "New folder");
    if (!name) return;
    await api.post(`/bids/${bidId}/folders`, { name, parent_id: active || null });
    loadFolders();
  };
  const delFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}" and everything inside it?`)) return;
    await api.delete(`/bids/${bidId}/folders/${f.folder_id}`);
    const leaving = pathIds.includes(f.folder_id);
    if (leaving) go(f.parent_id || "");
    loadFolders();
    if (!leaving) loadDocs();
  };
  const delDoc = async (d) => {
    if (!window.confirm(`Remove "${d.name}"?`)) return;
    await api.delete(`/bids/${bidId}/documents/${d.document_id}`);
    if (openDoc?.document_id === d.document_id) go(active);
    loadDocs();
  };
  const download = (d) => window.open(`${API_BASE}/documents/${d.document_id}/download?inline=true`, "_blank");
  const openDrive = async (d) => {
    try { const { data } = await api.get(`/bids/${bidId}/documents/${d.document_id}/drive-link`); window.open(data.url, "_blank"); }
    catch { toast.error("No Google Drive link for this file"); }
  };
  const renameFolder = async (f, name) => {
    const nm = (name || "").trim();
    setRenamingFolder(null);
    if (!nm || nm === f.name) return;
    setFolders((fs) => fs.map((x) => x.folder_id === f.folder_id ? { ...x, name: nm } : x));
    try { await api.put(`/bids/${bidId}/folders/${f.folder_id}`, { name: nm }); }
    catch { toast.error("Could not rename folder"); loadFolders(); }
  };
  const renameDoc = async (d, name) => {
    const nm = (name || "").trim();
    if (!nm || nm === d.name) return;
    setDocs((ds) => ds.map((x) => x.document_id === d.document_id ? { ...x, name: nm } : x));
    try { await api.put(`/bids/${bidId}/documents/${d.document_id}`, { name: nm }); }
    catch { toast.error("Could not rename document"); loadDocs(); }
  };
  const syncDrive = async (silent = false) => {
    try {
      const { data } = await api.post(`/bids/${bidId}/documents/sync-drive`);
      setDriveEnabled(!!data.enabled);
      if (!data.enabled) { if (!silent) toast.info("Google Drive isn't connected."); return; }
      const n = (data.updated?.length || 0);
      if (n > 0) { await loadFolders(); await loadDocs(); }
      if (!silent) toast.success(n > 0 ? `Synced ${n} name${n === 1 ? "" : "s"} from Drive` : "Already in sync with Drive");
    } catch { if (!silent) toast.error("Drive sync failed"); }
  };
  const openDriveFolder = async () => {
    try {
      const { data } = await api.get(`/bids/${bidId}/drive-folder`);
      if (!data.enabled) { toast.info("Google Drive isn't connected for this workspace."); return; }
      setDriveEnabled(true);
      setDriveFolder(data);
      setDriveDialog(true);
    } catch { toast.error("Could not load the Drive folder link"); }
  };
  const copyDriveLink = async () => {
    const url = driveFolder?.web_link;
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Couldn't copy — select the link and copy manually"); }
  };
  const ingestDrive = async () => {
    setIngestingDrive(true);
    setDriveProgress({ phase: "scanning" });
    try {
      const { data } = await api.post(`/bids/${bidId}/drive-folder/ingest`);
      const jobId = data.job_id;
      let misses = 0;
      for (;;) {
        await new Promise((r) => setTimeout(r, 1500));
        let st;
        try { st = (await api.get(`/bids/${bidId}/uploads/${jobId}/status`)).data; misses = 0; }
        catch { if (++misses >= 20) { toast.info("Still importing from Drive in the background — files will appear shortly."); break; } continue; }
        setDriveProgress(st);
        if (st.status === "done") {
          toast.success(st.count ? `Synced ${st.count} file${st.count === 1 ? "" : "s"} from Drive` : "Drive folder already in sync");
          if (st.skipped?.length) toast.warning(`${st.skipped.length} file${st.skipped.length === 1 ? "" : "s"} couldn’t be added`);
          await loadFolders(); await loadDocs();
          break;
        }
        if (st.status === "failed") { toast.error(st.error || "Drive import failed"); break; }
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not import from Drive"); }
    finally { setIngestingDrive(false); setDriveProgress(null); }
  };
  // Pull renames from Drive once when the tab opens (Drive → App) and learn if Drive is connected.
  useEffect(() => {
    if (syncedOnce.current) return;
    syncedOnce.current = true;
    syncDrive(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const searchTimer = useRef(null);

  const runSearch = (q) => {
    setSearch(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q.trim()) { setResults(null); return; }
    searchTimer.current = setTimeout(async () => {
      try { const { data } = await api.get(`/bids/${bidId}/doc-search`, { params: { q } }); setResults(data); }
      catch { setResults(null); }
    }, 300);
  };

  // Explorer-style location: a folder, or a plan set opened like a folder
  const fby = Object.fromEntries(folders.map((f) => [f.folder_id, f]));
  const pathIds = [];
  for (let id = active, hops = 0; id && fby[id] && hops < 50; id = fby[id].parent_id, hops++) pathIds.unshift(id);
  const subfolders = folders.filter((f) => (f.parent_id || null) === (active || null));
  const loadingDocs = docsAt !== active;
  const shownDocs = loadingDocs ? [] : docs;
  const docNow = openDoc ? (docs.find((d) => d.document_id === openDoc.document_id) || openDoc) : null;
  const docSheets = docNow ? sheetsMap[docNow.document_id] : null;

  const apply = ({ f, d }, pick = null) => {
    setActive(f); setOpenDoc(d); setSelected(pick); setRenaming(null);
    setSearch(""); setResults(null);
  };
  const go = (f, d = null, pick = null) => {
    const sd = slim(d);
    apply({ f, d: sd }, pick);
    const cur = hist.stack[hist.i];
    if (cur && cur.f === f && (cur.d?.document_id || null) === (sd?.document_id || null)) return;
    setHist((h) => ({ stack: [...h.stack.slice(Math.max(0, h.i - 48), h.i + 1), { f, d: sd }], i: Math.min(h.i + 1, 49) }));
  };
  // a remembered or history folder that no longer exists falls back to the top
  useEffect(() => { if (foldersReady && active && !folders.some((f) => f.folder_id === active)) go(""); }, [foldersReady, folders, active]); // eslint-disable-line react-hooks/exhaustive-deps
  const back = () => { if (hist.i > 0) { apply(hist.stack[hist.i - 1]); setHist((h) => ({ ...h, i: h.i - 1 })); } };
  const fwd = () => { if (hist.i < hist.stack.length - 1) { apply(hist.stack[hist.i + 1]); setHist((h) => ({ ...h, i: h.i + 1 })); } };
  const up = () => {
    if (openDoc) go(active, null, `d:${openDoc.document_id}`);
    else if (active) go(fby[active]?.parent_id || "", null, `f:${active}`);
  };
  const openDocItem = (d) => (!d.sheet_count ? download(d) : go(d.folder_id || "", d));
  const openSheet = (s, q) => onOpenSheet && onOpenSheet(s, q);

  const crumbs = [{ key: "root", label: "All documents", f: "" }, ...pathIds.map((id) => ({ key: id, label: fby[id].name, f: id }))];
  if (docNow) crumbs.push({ key: `d:${docNow.document_id}`, label: docNow.name, f: active, d: docNow });

  const fItems = (fs, rename) => fs.map((f) => ({ key: `f:${f.folder_id}`, open: () => go(f.folder_id), rename }));
  const dItems = (ds, rename) => ds.map((d) => ({ key: `d:${d.document_id}`, open: () => openDocItem(d), rename }));
  const sItems = (ss, q, rename) => ss.map((s) => ({ key: `s:${s.sheet_id}`, open: () => openSheet(s, q), rename }));
  const items = results ? [...dItems(results.documents || [], false), ...sItems(results.sheets || [], search, false)]
    : docNow ? sItems(docSheets || [], "", view !== "grid")
    : [...fItems(subfolders, view !== "grid"), ...dItems(shownDocs, view !== "grid")];

  const onKey = (e) => {
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
    if (e.altKey) { const m = { ArrowLeft: back, ArrowRight: fwd, ArrowUp: up }[e.key]; if (m) { e.preventDefault(); m(); } return; }
    const i = items.findIndex((x) => x.key === selected);
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
    if (step && items.length) { e.preventDefault(); setSelected(items[Math.min(items.length - 1, Math.max(0, i + step))].key); return; }
    if (e.key === "Enter" && i >= 0) { e.preventDefault(); items[i].open(); }
    else if (e.key === "Backspace") { e.preventDefault(); up(); }
    else if (e.key === "F2" && i >= 0 && !readOnly && items[i].rename) { e.preventDefault(); setRenaming(items[i].key); }
  };

  const selectK = (k) => () => setSelected(k);
  const endRename = () => { setRenaming(null); explorerRef.current?.focus(); };
  const folderItem = (f) => {
    const k = `f:${f.folder_id}`;
    return (
      <FolderItem key={k} f={f} subCount={folders.filter((x) => x.parent_id === f.folder_id).length} selected={selected === k}
        gestureProps={gesture(selectK(k), () => go(f.folder_id))} renaming={renaming === k} readOnly={readOnly} canDelete={canDelete}
        onRenameStart={() => setRenaming(k)} onRename={(v) => { endRename(); renameFolder(f, v); }} onRenameCancel={endRename} onDelete={() => delFolder(f)} />
    );
  };
  const docItem = (d, header = false, inSearch = false) => {
    const k = `d:${d.document_id}`;
    return (
      <DocItem key={k} d={d} header={header} selected={!header && selected === k} gestureProps={header ? null : gesture(selectK(k), () => openDocItem(d))}
        renaming={renaming === k} readOnly={readOnly} canDelete={canDelete}
        onRenameStart={() => setRenaming(k)} onRename={inSearch ? null : (v) => { endRename(); renameDoc(d, v); }} onRenameCancel={endRename}
        onDownload={download} onOpenDrive={openDrive} onDelete={delDoc} onAiName={aiName} onOcr={runOcr} onHistory={setHistoryOf} />
    );
  };
  const sheetItem = (s, q, snippet) => {
    const k = `s:${s.sheet_id}`;
    return (
      <SheetItem key={k} s={s} snippet={snippet} selected={selected === k} gestureProps={gesture(selectK(k), () => openSheet(s, q))}
        renaming={renaming === k} readOnly={readOnly} onRenameStart={results ? null : () => setRenaming(k)} onRenameCancel={endRename}
        onRename={(v) => { endRename(); const nm = v.trim(); if (nm && nm !== s.name) renameSheet(s.document_id, s.sheet_id, nm); }}
        onOpen={onOpenSheet ? () => openSheet(s, q) : null} />
    );
  };
  const sheetTile = (s) => <SheetTile key={`s:${s.sheet_id}`} s={s} selected={selected === `s:${s.sheet_id}`} gestureProps={gesture(selectK(`s:${s.sheet_id}`), () => openSheet(s, ""))} />;
  const docTile = (d) => <DocTile key={`d:${d.document_id}`} d={d} selected={selected === `d:${d.document_id}`} gestureProps={gesture(selectK(`d:${d.document_id}`), () => openDocItem(d))} onHistory={setHistoryOf} />;
  const docsLoadingRow = loadingDocs && <div className="col-span-full text-xs text-slate-400 flex items-center gap-2 px-3 py-3" data-testid="doc-list-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>;
  const folderTile = (f) => <FolderTile key={`f:${f.folder_id}`} f={f} selected={selected === `f:${f.folder_id}`} gestureProps={gesture(selectK(`f:${f.folder_id}`), () => go(f.folder_id))} />;

  return (
    <div className="flex h-full" data-testid="documents-tab">
      <FolderPane folders={folders} active={active} pathIds={pathIds} readOnly={readOnly} canDelete={canDelete}
        onOpen={(fid) => go(fid)} onNew={newFolder} renamingFolder={renamingFolder} setRenamingFolder={setRenamingFolder}
        onRename={renameFolder} onDelete={delFolder} />

      {/* main */}
      <div className="flex-1 min-w-0 flex flex-col relative"
        onDragOver={(e) => { if (readOnly) return; e.preventDefault(); if (!dragOver) setDragOver(true); }}
        onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget)) return; setDragOver(false); }}
        onDrop={async (e) => {
          if (readOnly) return;
          e.preventDefault(); setDragOver(false);
          const files = Array.from(e.dataTransfer?.files || []);
          for (const f of files) await uploadOne(f);
        }}>
        {dragOver && !readOnly && (
          <div className="absolute inset-0 z-20 bg-[#003E87]/10 border-2 border-dashed border-[#003E87] rounded-lg flex items-center justify-center pointer-events-none" data-testid="doc-drop-overlay">
            <div className="text-sm font-medium text-[#003E87] flex items-center gap-2"><Upload className="w-5 h-5" /> Drop files to upload to this project</div>
          </div>
        )}
        <ReceivedUnderNdaDialog open={!!ndaAsk} filename={ndaAsk?.filename} onAnswer={(v) => { ndaAsk?.resolve(v); setNdaAsk(null); }} />
        <div className="shrink-0 border-b border-slate-200 p-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => runSearch(e.target.value)} placeholder="Search documents & sheets…" className="w-full text-sm border border-slate-200 rounded pl-8 pr-3 py-2" data-testid="doc-search-input" />
          </div>
          <div className="flex items-center rounded-md border border-slate-200 overflow-hidden shrink-0" data-testid="doc-view-toggle">
            <button onClick={() => setView("list")} className={`px-2 py-1.5 ${view === "list" ? "bg-[#003E87] text-white" : "text-slate-500 hover:bg-slate-50"}`} title="List view" data-testid="doc-view-list"><ListIcon className="w-4 h-4" /></button>
            <button onClick={() => setView("grid")} className={`px-2 py-1.5 ${view === "grid" ? "bg-[#003E87] text-white" : "text-slate-500 hover:bg-slate-50"}`} title="Large icons" data-testid="doc-view-grid"><LayoutGrid className="w-4 h-4" /></button>
          </div>
          {!readOnly && (
            <>
              {!docNow && (
                <Button size="sm" variant="outline" className="text-xs md:hidden" onClick={newFolder} data-testid="doc-new-folder-mobile">
                  <FolderPlus className="w-4 h-4 mr-1.5" /> New folder
                </Button>
              )}
              {docs.length > 0 && !jobActive && (
                <Button size="sm" variant="outline" className="text-xs" onClick={processAll} data-testid="doc-process-all" title="Auto-name every sheet from its title block and OCR any scanned pages so the whole packet is searchable">
                  <Sparkles className="w-4 h-4 mr-1.5" /> Auto-name + index all
                </Button>
              )}
              {jobActive && (
                <Button size="sm" variant="outline" className="text-xs border-rose-300 text-rose-600 hover:bg-rose-50" onClick={cancelProcessing} data-testid="doc-cancel-processing" title="Stop the auto-name / OCR run">
                  <X className="w-4 h-4 mr-1.5" /> Cancel processing
                </Button>
              )}
              <Button size="sm" variant="outline" className="text-xs" onClick={openDriveFolder} data-testid="doc-drive-folder-btn" title="Open or share this project's Google Drive folder — drop huge files straight into Drive, then pull them in">
                <HardDrive className="w-4 h-4 mr-1.5" /> Drive folder
              </Button>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer select-none px-1 py-1 rounded border border-slate-200" title="Skip extracting/splitting — stream the file straight to Google Drive as-is. Best for huge multi-GB zips that don't need to be split into takeoff sheets." data-testid="doc-express-toggle-label">
                <input type="checkbox" checked={rawUpload} onChange={(e) => setRawUpload(e.target.checked)} className="accent-[#003E87]" data-testid="doc-express-toggle" />
                <Cloud className="w-3.5 h-3.5" /> Express to Drive
              </label>
              <input ref={fileRef} type="file" multiple accept="*/*" className="hidden" onChange={onPick} data-testid="doc-upload-input" />
              <Button size="sm" className="bg-[#F4C400] text-black hover:brightness-95" onClick={() => fileRef.current?.click()} disabled={!!uploading} data-testid="doc-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                {uploading ? `${uploading.phase || `${uploading.pct}%`} · ${uploading.name.slice(0, 18)}` : "Upload files / .zip"}
              </Button>
            </>
          )}
        </div>

        <div className="shrink-0 border-b border-slate-200 px-3 py-2">
          <AddressBar crumbs={crumbs} canBack={hist.i > 0} canFwd={hist.i < hist.stack.length - 1} canUp={!!(active || docNow)}
            onBack={back} onFwd={fwd} onUp={up} onCrumb={(c) => go(c.f, c.d || null)} />
        </div>

        {progress && jobActive && (
          <div className="shrink-0 border-b border-slate-200 px-3 py-2 bg-[#F4C400]/10" data-testid="doc-progress-bar">
            <div className="flex items-center justify-between text-[11px] text-[#8a6d00] mb-1">
              <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Naming &amp; indexing sheets…</span>
              <div className="flex items-center gap-3">
                <span className="font-mono" data-testid="doc-progress-label">{progress.named}/{progress.total} named · {progress.ocr_done} of {progress.ocr_done + progress.needs_ocr} searchable</span>
                <button onClick={cancelProcessing} className="text-rose-600 hover:underline flex items-center gap-0.5" data-testid="doc-progress-cancel"><X className="w-3 h-3" /> Cancel</button>
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-[#F4C400]/25 overflow-hidden">
              <div className="h-full bg-[#F4C400] transition-all duration-500" style={{ width: `${progress.total ? Math.round((progress.named / progress.total) * 100) : 0}%` }} />
            </div>
          </div>
        )}

        <div ref={explorerRef} className="flex-1 overflow-auto p-4 focus:outline-none" tabIndex={0} onKeyDown={onKey} onClick={() => setSelected(null)} data-testid="doc-explorer">
          {results ? (
            <div className="space-y-1" data-testid="doc-search-results">
              <div className="text-xs text-slate-500 mb-2">{(results.documents?.length || 0)} document(s), {(results.sheets?.length || 0)} sheet(s) matching "{search}"</div>
              {results.documents?.map((d) => docItem(d, false, true))}
              {results.sheets?.map((s) => sheetItem(s, search, s.snippet))}
            </div>
          ) : docNow ? (
            <div className="space-y-3" data-testid={`doc-sheets-${docNow.document_id}`}>
              {docItem(docNow, true)}
              {docSheets == null ? (
                <div className="text-sm text-slate-400 flex items-center justify-center py-8"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading sheets…</div>
              ) : docSheets.length === 0 ? (
                <div className="text-sm text-slate-400 py-8 text-center" data-testid="doc-no-sheets">This file has no sheets. Use the download button to open it.</div>
              ) : view === "grid" ? (
                <div className={GRID} data-testid="doc-grid">{docSheets.map(sheetTile)}</div>
              ) : (
                <div className="space-y-1" data-testid="doc-sheet-list">{docSheets.map((s) => sheetItem(s, ""))}</div>
              )}
            </div>
          ) : subfolders.length === 0 && !loadingDocs && docs.length === 0 ? (
            active === "" ? (
              <div className="h-full flex flex-col items-center justify-center gap-3 text-center">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center"><Upload className="w-5 h-5 text-slate-400" /></div>
                <div className="text-sm text-slate-600">Upload drawings, plan sets, spreadsheets or a whole .zip packet.</div>
                <div className="text-xs text-slate-400">Large files upload in chunks. Zips keep their folder structure. PDFs, Word, Excel & text files split into sheets automatically — open any of them in Takeoff to annotate.</div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center gap-2 text-center" data-testid="doc-folder-empty">
                <FolderOpen className="w-8 h-8 text-slate-300" />
                <div className="text-sm text-slate-500">This folder is empty.</div>
                {!readOnly && <div className="text-xs text-slate-400">Upload or drop files here to add them to this folder.</div>}
              </div>
            )
          ) : view === "grid" ? (
            <div className={GRID} data-testid="doc-grid">
              {subfolders.map(folderTile)}
              {shownDocs.map(docTile)}
              {docsLoadingRow}
            </div>
          ) : (
            <div className="space-y-1" data-testid="doc-list">
              {subfolders.map(folderItem)}
              {shownDocs.map((d) => docItem(d))}
              {docsLoadingRow}
            </div>
          )}
        </div>
        <div className="shrink-0 border-t border-slate-200 px-3 py-1.5 text-[11px] text-slate-500 flex items-center gap-3" data-testid="doc-status-bar">
          <span data-testid="doc-status-count">{items.length} item{items.length === 1 ? "" : "s"}</span>
          {selected && <span data-testid="doc-status-selected">1 selected</span>}
          <span className="ml-auto text-slate-400 hidden md:inline">Double-click to open · Backspace to go up</span>
        </div>
      </div>

      <DocHistoryDialog doc={historyOf} onClose={() => setHistoryOf(null)} onOpen={download} />
      <Dialog open={driveDialog} onOpenChange={setDriveDialog}>
        <DialogContent data-testid="doc-drive-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><HardDrive className="w-4 h-4 text-[#003E87]" /> Project Google Drive folder</DialogTitle>
            <DialogDescription>Drop very large files straight into this Drive folder (no upload limits), then sync them here. Big files stay in Drive and stream on demand — the app never downloads multi-GB files to itself.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Shareable folder link</div>
              <div className="flex items-center gap-2">
                <Input readOnly value={driveFolder?.web_link || ""} className="text-xs" data-testid="doc-drive-link" onFocus={(e) => e.target.select()} />
                <Button size="sm" variant="outline" onClick={copyDriveLink} disabled={!driveFolder?.web_link} data-testid="doc-drive-copy" title="Copy link">
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => driveFolder?.web_link && window.open(driveFolder.web_link, "_blank")} disabled={!driveFolder?.web_link} data-testid="doc-drive-open" title="Open in Google Drive"><ExternalLink className="w-4 h-4" /></Button>
              </div>
              <div className="text-[11px] text-slate-400 mt-1">Anyone with the link can add files. Share it with a third party to collect huge plan sets without sending them to you.</div>
            </div>
            <div className="border-t border-slate-100 pt-3">
              <Button size="sm" className="bg-[#003E87] text-white hover:brightness-110 w-full" onClick={ingestDrive} disabled={ingestingDrive} data-testid="doc-drive-ingest">
                {ingestingDrive ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                {ingestingDrive ? "Syncing from Drive…" : "Sync files from Drive into this project"}
              </Button>
              {ingestingDrive && driveProgress ? (
                <div className="mt-3" data-testid="doc-drive-progress"><DriveProgress p={driveProgress} /></div>
              ) : (
                <div className="text-[11px] text-slate-400 mt-1.5 text-center">New files are pulled in. Large files (and big zips) are attached as links and <span className="text-[#003E87]">stream directly from Drive</span> — never downloaded to the app. Smaller PDFs are split into takeoff sheets.</div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DriveProgress({ p }) {
  const phase = p.phase;
  let label = "Scanning your Drive folder…";
  let pct = null;
  if (phase === "downloading") { label = `Downloading ${p.current || "file"}${p.dl_pct != null ? ` ${p.dl_pct}%` : ""}…`; pct = p.dl_pct ?? null; }
  else if (phase === "linking") { label = `Attaching ${p.current || "file"} — streams from Drive`; }
  else if (phase === "importing") {
    if (p.sub_total) { label = `Importing ${p.current || "files"} — ${p.sub_done || 0}/${p.sub_total}`; pct = Math.round(((p.sub_done || 0) / p.sub_total) * 100); }
    else { label = `Importing ${p.current || "file"}…`; }
  } else if (phase === "done") { label = "Finishing up…"; }
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-[#003E87] mb-1 gap-2">
        <span className="flex items-center gap-1.5 min-w-0"><Loader2 className="w-3 h-3 animate-spin shrink-0" /> <span className="truncate" data-testid="doc-drive-progress-label">{label}</span></span>
        {p.files_total ? <span className="font-mono shrink-0" data-testid="doc-drive-progress-files">{Math.min((p.files_done || 0) + 1, p.files_total)}/{p.files_total}</span> : null}
      </div>
      <div className="h-1.5 rounded-full bg-[#003E87]/15 overflow-hidden">
        <div className={`h-full bg-[#003E87] ${pct == null ? "w-1/3 animate-pulse" : "transition-all duration-500"}`} style={pct == null ? undefined : { width: `${pct}%` }} />
      </div>
      {p.count ? <div className="text-[11px] text-slate-400 mt-1" data-testid="doc-drive-progress-count">{p.count} file{p.count === 1 ? "" : "s"} added so far</div> : null}
    </div>
  );
}
